import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { 
  UserPlus, Copy, Gift, Coins, CheckCircle2, Share2, 
  Loader2, Users, Trophy, ArrowRight 
} from 'lucide-react';
import { toast } from 'sonner';

export default function ReferralPage() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [codeRes, statsRes, historyRes] = await Promise.all([
        api.get('/referral/my-code'),
        api.get('/referral/stats'),
        api.get('/referral/history').catch(() => ({ data: [] })),
      ]);
      
      setStats({
        code: codeRes.data.code,
        total_referrals: statsRes.data.total_referrals || 0,
        successful_referrals: statsRes.data.successful_referrals || 0,
        pending_referrals: statsRes.data.pending_referrals || 0,
        total_rewards: statsRes.data.total_rewards || 0,
        pointsPerReferral: 100, // Default value
        discountPercent: 20, // Default value
      });
      setHistory(Array.isArray(historyRes.data) ? historyRes.data : (historyRes.data?.results || []));
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (stats?.code) {
      navigator.clipboard.writeText(stats.code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareReferral = () => {
    if (navigator.share && stats?.code) {
      navigator.share({
        title: 'Join Ready Set STEM!',
        text: `Use my referral code ${stats.code} to get 20% off your first STEM class at Ready Set STEM!`,
        url: window.location.origin,
      }).catch(() => {
        // User cancelled or error - copy to clipboard instead
        copyToClipboard();
      });
    } else {
      copyToClipboard();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="referral-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-rsc-navy">Referral Program</h1>
        <p className="text-muted-foreground">
          Earn points by referring friends and family to Ready Set STEM
        </p>
      </div>

      {/* Main Referral Card */}
      <Card className="overflow-hidden border-2 border-rsc-yellow">
        <div className="bg-gradient-to-r from-rsc-blue to-rsc-blue-light p-8 text-white">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
              <UserPlus className="w-8 h-8" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold">Your Referral Code</h2>
              <p className="text-white/80">Share this code with friends and family</p>
            </div>
          </div>

          <div className="bg-white/10 rounded-xl p-4 flex items-center justify-between gap-4 mb-6">
            <code className="font-mono text-3xl font-bold tracking-wider" data-testid="referral-code">
              {stats?.code || 'Loading...'}
            </code>
            <div className="flex gap-2">
              <Button 
                onClick={copyToClipboard}
                className="bg-white text-rsc-navy hover:bg-white/90"
                data-testid="copy-code-btn"
              >
                {copied ? <CheckCircle2 className="h-5 w-5 mr-2" /> : <Copy className="h-5 w-5 mr-2" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button 
                onClick={shareReferral}
                className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright"
                data-testid="share-code-btn"
              >
                <Share2 className="h-5 w-5 mr-2" />
                Share
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <Coins className="w-6 h-6 mx-auto mb-2 text-rsc-yellow" />
              <p className="font-bold text-2xl">{stats?.pointsPerReferral || 50}</p>
              <p className="text-sm text-white/70">Points per Referral</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <Gift className="w-6 h-6 mx-auto mb-2 text-rsc-yellow" />
              <p className="font-bold text-2xl">{stats?.discountPercent || 20}%</p>
              <p className="text-sm text-white/70">Friend's Discount</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 text-rsc-yellow" />
              <p className="font-bold text-2xl">∞</p>
              <p className="text-sm text-white/70">No Limit</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="card-dashboard" data-testid="stat-referrals">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Referrals</p>
                <p className="text-3xl font-bold text-rsc-navy">{stats?.total_referrals || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-rsc-blue/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-rsc-blue" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard" data-testid="stat-successful">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Successful</p>
                <p className="text-3xl font-bold text-green-600">{stats?.successful_referrals || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard" data-testid="stat-pending">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-3xl font-bold text-rsc-yellow">{stats?.pending_referrals || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-rsc-yellow/20 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-rsc-yellow" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard" data-testid="stat-earned">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Points Earned</p>
                <p className="text-3xl font-bold text-rsc-orange">{stats?.total_points_earned || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-rsc-orange/10 flex items-center justify-center">
                <Coins className="w-6 h-6 text-rsc-orange" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How It Works */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-rsc-yellow" />
            How It Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'Share Your Code', desc: 'Copy and share your unique referral code with friends' },
              { step: '2', title: 'Friend Signs Up', desc: 'They create an account and apply your code' },
              { step: '3', title: 'They Enroll', desc: 'Friend gets 20% off their first course enrollment' },
              { step: '4', title: 'You Earn Points', desc: 'You receive 50 bonus points to spend on rewards' },
            ].map((item, index) => (
              <div key={item.step} className="relative text-center" data-testid={`how-step-${index}`}>
                <div className="w-12 h-12 rounded-full bg-rsc-navy text-white flex items-center justify-center font-bold text-xl mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold text-rsc-navy mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
                {index < 3 && (
                  <ArrowRight className="hidden md:block absolute top-5 -right-3 w-6 h-6 text-gray-300" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Referral History */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-rsc-blue" />
            Referral History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8">
              <UserPlus className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No referrals yet</p>
              <p className="text-sm text-muted-foreground">
                Start sharing your code to earn bonus points!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((referral, index) => (
                <div
                  key={referral.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                  data-testid={`referral-history-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-rsc-blue/10 flex items-center justify-center">
                      <UserPlus className="w-5 h-5 text-rsc-blue" />
                    </div>
                    <div>
                      <p className="font-medium">{referral.referred_user_name || 'Friend'}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(referral.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge 
                      variant={referral.status === 'completed' ? 'default' : 'secondary'}
                      className={referral.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}
                    >
                      {referral.status === 'completed' ? 'Completed' : 'Pending'}
                    </Badge>
                    {referral.status === 'completed' && (
                      <p className="text-sm text-green-600 font-medium mt-1">
                        +{stats?.pointsPerReferral || 50} pts
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
